<template>
  <div class="space-y-8">
    <div>
      <label for="expectation" class="block mb-2 font-semibold">
        {{ $t("form.step2.expectation.label") }}
        <span class="text-brand-red">*</span>
      </label>
      <select
        id="expectation"
        :value="formData.expectation"
        @change="updateField('expectation', $event.target.value)"
        :class="[
          'w-full p-3 bg-white border rounded-lg outline-none focus:ring-2 focus:ring-brand-red/50 focus:border-brand-red transition-all',
          errors.expectation ? 'border-red-500' : 'border-gray-300',
        ]"
      >
        <option value="yes">{{ $t("form.step2.expectation.optionYes") }}</option>
        <option value="no">{{ $t("form.step2.expectation.optionNo") }}</option>
      </select>
      <p v-if="errors.expectation" class="mt-1 text-xs text-red-500">
        {{ errors.expectation }}
      </p>
    </div>

    <div>
      <label class="block mb-2 font-semibold">
        {{ $t("form.step2.rating.label") }}
        <span class="text-brand-red">*</span>
      </label>
      <StarRating
        :value="formData.rating"
        @input="updateField('rating', $event)"
      />
      <p v-if="errors.rating" class="mt-1 text-xs text-red-500">
        {{ errors.rating }}
      </p>
    </div>

    <div v-if="formData.expectation === 'yes'">
      <label for="features" class="block mb-2 font-semibold"
        >{{ $t("form.step2.features.label") }}
        <span class="text-brand-red">*</span></label
      >
      <textarea
        id="features"
        name="features"
        :value="formData.features"
        @input="updateField('features', $event.target.value)"
        rows="4"
        :placeholder="$t('form.step2.features.placeholder')"
        :class="[
          'w-full p-3 bg-white border rounded-lg outline-none focus:ring-2 focus:ring-brand-red/50 focus:border-brand-red transition-all',
          errors.features ? 'border-red-500' : 'border-gray-300',
        ]"
      ></textarea>
      <p v-if="errors.features" class="mt-1 text-xs text-red-500">
        {{ errors.features }}
      </p>
    </div>
    <div v-else class="grid grid-cols-1 gap-6 md:grid-cols-2">
      <div>
        <label for="comment" class="block mb-2 font-semibold">
          {{ $t("form.describeIssue") }}  <span class="text-brand-red">*</span>
        </label>
        <textarea
          id="comment"
          name="comment"
          :value="formData.comment"
          @input="updateField('comment', $event.target.value)"
          rows="6"
          :class="[
            'w-full p-3 bg-white border rounded-2xl outline-none focus:ring-2 focus:ring-brand-red/50 focus:border-brand-red transition-all',
            errors.comment ? 'border-red-500' : 'border-gray-200',
          ]"
        ></textarea>
        <p class="mt-1 text-xs text-brand-gray">
          {{ $t("form.commentHelp") }}
        </p>
        <p v-if="errors.comment" class="mt-1 text-xs text-red-500">
          {{ errors.comment }}
        </p>
      </div>
      <div>
        <label class="block mb-2 font-semibold"
          >{{ $t("form.fileUpload") }}
          </label
        >
        <div
          :class="[
            'bg-white p-4 border rounded-2xl',
            errors.files ? 'border-red-500' : 'border-gray-200',
          ]"
        >
          <label
            for="file-upload"
            :class="[
              'flex flex-col items-center justify-center w-full py-6 border-2 border-gray-300 border-dashed rounded-xl cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors',
            ]"
            @dragover.prevent
            @drop.prevent="onDrop"
          >
            <div class="flex flex-col items-center justify-center text-center">
              <UploadIcon />
              <i18n
                path="form.fileUploadInstructions"
                tag="p"
                class="mb-2 text-sm text-brand-gray"
              >
                <span slot="action" class="font-semibold text-brand-red">{{
                  $t("form.fileUploadInstructionsAction")
                }}</span>
              </i18n>
              <p class="text-xs text-gray-500">
                {{ $t("form.fileUploadLimit") }}
              </p>
            </div>
            <input
              id="file-upload"
              type="file"
              class="hidden"
              multiple
              @change="handleFileChange"
              accept="image/*,video/*,.xlsx,.pdf,.xls,.docx,.doc,.ppt,.pptx"
            />
          </label>
          <div class="mt-4 space-y-3" v-if="formData.files.length > 0">
            <div
              v-for="file in formData.files"
              :key="file.id"
              class="p-2.5 bg-white rounded-lg border border-gray-200"
            >
              <div class="flex items-center gap-3">
                <div class="flex items-center justify-center flex-shrink-0 w-12 h-12 overflow-hidden text-gray-600 bg-gray-100 rounded-md">
                  <img
                    v-if="determineFileType(file) === 'image'"
                    :src="file.previewUrl"
                    :alt="file.name"
                    class="object-cover w-full h-full"
                  />
                  <video
                    v-else-if="determineFileType(file) === 'video'"
                    :src="file.previewUrl"
                    class="object-cover w-full h-full"
                    muted
                    playsinline
                  ></video>
                  <template v-else>
                    <svg v-if="determineFileType(file) === 'pdf'" xmlns="http://www.w3.org/2000/svg" class="w-7 h-7" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V8.414a1 1 0 00-.293-.707l-4.414-4.414A1 1 0 0011.586 2H4zm6 6a2 2 0 100-4 2 2 0 000 4zm-1 3a1 1 0 00-1 1v1a1 1 0 102 0v-1a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                    <svg v-else-if="determineFileType(file) === 'word'" xmlns="http://www.w3.org/2000/svg" class="w-7 h-7" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V8.414a1 1 0 00-.293-.707l-4.414-4.414A1 1 0 0011.586 2H4zm7 10V9a1 1 0 00-2 0v3a1 1 0 002 0zM7 9a1 1 0 011-1h2a1 1 0 110 2H8a1 1 0 01-1-1z" clip-rule="evenodd" />
                    </svg>
                    <svg v-else-if="determineFileType(file) === 'excel'" xmlns="http://www.w3.org/2000/svg" class="w-7 h-7" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V8.414a1 1 0 00-.293-.707l-4.414-4.414A1 1 0 0011.586 2H4zm5 10H7v-2h2v2zm0-3H7V7h2v2zm3 3h-2v-2h2v2zm0-3h-2V7h2v2z" clip-rule="evenodd" />
                    </svg>
                    <svg v-else-if="determineFileType(file) === 'powerpoint'" xmlns="http://www.w3.org/2000/svg" class="w-7 h-7" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M4 2a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V8.414a1 1 0 00-.293-.707l-4.414-4.414A1 1 0 0011.586 2H4zm4 6a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1zm-1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clip-rule="evenodd" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" class="w-7 h-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                  </template>
                </div>
                <div class="flex-grow overflow-hidden">
                  <p class="text-sm font-semibold truncate text-brand-dark">
                    {{ file.name }}
                  </p>
                  <p class="text-xs text-brand-gray">{{ file.size }}</p>
                </div>
                <button
                  v-if="file.state === 'completed'"
                  @click.prevent="removeFile(file.id)"
                  class="flex-shrink-0 p-1 text-gray-400 hover:text-brand-red"
                >
                  <CloseIcon />
                </button>
              </div>
              <div
                v-if="file.state === 'uploading'"
                class="w-full h-1 mt-2 bg-gray-200 rounded-full"
              >
                <div
                  class="h-1 transition-all duration-300 rounded-full bg-brand-red"
                  :style="{ width: file.progress + '%' }"
                ></div>
              </div>
            </div>
          </div>
        </div>
        <p v-if="errors.files" class="mt-1 text-xs text-red-500">
          {{ errors.files }}
        </p>
      </div>
    </div>

    <div class="flex items-center justify-between pt-6">
      <button
        @click.prevent="onPrev"
        class="px-10 py-3 font-bold text-white transition-all bg-gray-500 rounded-lg hover:bg-gray-600"
      >
        {{ $t("form.previousButton") }}
      </button>
      <button
        @click.prevent="onSubmit"
        :disabled="loading"
        class="px-10 py-3 font-bold text-white transition-all rounded-lg bg-brand-red hover:bg-opacity-90"
        :class="{'opacity-75 cursor-not-allowed': loading}"
      >
          <SpinnerIcon v-if="loading" />
          <span v-else>{{ $t('form.nextButton') }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import StarRating from "./StarRating.vue";
import {UploadIcon,CloseIcon,SpinnerIcon } from "./icons/";

export default {
  name: "FormStep2",
  components: {
    StarRating,
    SpinnerIcon,
    CloseIcon,
    UploadIcon
  },
  props: {
    formData: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      default: () => ({}),
    },
    loading: {
        type: Boolean,
        default: false
    }
  },
  methods: {
    handleFileChange(e) {
      const target = e.target;
      if (target.files) {
        this.addFiles(target.files);
      }
      target.value = "";
    },
    onDragOver(e) {
      e.preventDefault();
    },
    onDrop(e) {
      e.preventDefault();
      if (e.dataTransfer?.files) {
        this.addFiles(e.dataTransfer.files);
      }
    },
    // ===== START: MODIFIED METHOD =====
    addFiles(fileList) {
      const maxSizeInBytes = 50 * 1024 * 1024; // 50MB
      const oversizedFiles = [];
      
      // 1. 筛选出有效的文件并记录超过大小限制的文件名
      const validFiles = Array.from(fileList).filter(file => {
        if (file.size > maxSizeInBytes) {
          oversizedFiles.push(file.name);
          return false;
        }
        return true;
      });

      // 2. 如果有文件太大，则提醒用户
      if (oversizedFiles.length > 0) {
        // 假设你使用了 i18n, 可以这样写:
        alert(this.$t("form.maxFileSizeError", { files: oversizedFiles.join('\n- ') }));
      }

      // 3. 检查文件总数限制
      const currentFiles = this.formData.files;
      if (currentFiles.length + validFiles.length > 5) {
        alert(this.$t("form.maxFilesError"));
        return;
      }
      
      // 如果没有有效文件可添加，则直接返回
      if(validFiles.length === 0) {
        return;
      }

      // 4. 只处理有效的文件
      const newFiles = validFiles.map((file) => ({
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        size: this.formatFileSize(file.size),
        previewUrl: URL.createObjectURL(file),
        progress: 0,
        state: "uploading",
      }));

      this.$emit("update-files", [...currentFiles, ...newFiles]);

      newFiles.forEach((file) => this.simulateUpload(file));
    },
    // ===== END: MODIFIED METHOD =====
    removeFile(fileId) {
      const fileToRemove = this.formData.files.find((f) => f.id === fileId);
      if (fileToRemove) {
        URL.revokeObjectURL(fileToRemove.previewUrl);
      }
      this.$emit("remove-file", fileId);
    },
    simulateUpload(fileToUpload) {
      const interval = setInterval(() => {
        const fileInArray = this.formData.files.find(
          (f) => f.id === fileToUpload.id
        );
        if (fileInArray) {
          if (fileInArray.progress >= 100) {
            fileInArray.state = "completed";
            clearInterval(interval);
          } else {
            fileInArray.progress += Math.floor(Math.random() * 15) + 5;
            if (fileInArray.progress > 100) fileInArray.progress = 100;
          }
          this.$emit("update-files", [...this.formData.files]);
        } else {
          clearInterval(interval);
        }
      }, 200);
    },
    formatFileSize(bytes, decimals = 1) {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
    },
    determineFileType(file) {
      if (file.file.type.startsWith("image/")) {
        return "image";
      }
      if (file.file.type.startsWith("video/")) {
        return "video";
      }
      
      const extension = file.name.split(".").pop().toLowerCase();
      switch (extension) {
        case "pdf":
          return "pdf";
        case "doc":
        case "docx":
          return "word";
        case "xls":
        case "xlsx":
          return "excel";
        case "ppt":
        case "pptx":
          return "powerpoint";
        default:
          return "generic";
      }
    },
    updateField(key, value) {
      this.$emit("update-form-data", { [key]: value });
    },
    onPrev() {
      this.$emit("prev-step");
    },
    onSubmit() {
      this.$emit("go-to-step3");
    },
  },
};
</script>